import React, { useState, useEffect, useRef, useMemo } from "react";
import { FaSearch, FaFilter, FaCheck, FaTimes } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { useSelector } from "react-redux";
import { arClass } from "../../utils/useArClass";

const PriceRangeSelector = ({
  filters = {},
  onChange = () => {},
  darkMode = false,
  priceRanges = [
    { label: "Tous", min: 0, max: 300000 },
    { label: "0 - 5 000 DH", min: 0, max: 5000 },
    { label: "5 001 - 10 000 DH", min: 5001, max: 10000 },
    { label: "10 001 - 20 000 DH", min: 10001, max: 20000 },
    { label: "20 001 - 50 000 DH", min: 20001, max: 50000 },
    { label: "50 001 DH et plus", min: 50001, max: 300000 },
  ],
}) => {
  const handleSelect = (range) => {
    onChange(range.min, range.max);
  };
  const defaultLanguage = useSelector((state) => state.settings.defaultLanguage); 

  return (
    <div className="mb-6">
      <p className={arClass("uppercase text-xs font-semibold mb-3 opacity-70")}>
        {
          defaultLanguage === "ar" ? "تصفية حسب السعر" :defaultLanguage === "fr" ? "Filtrer par prix" : "Filter by Price"
        }
      </p>
      <div className={arClass("flex flex-col gap-2")}>
        {priceRanges.map((range, index) => (
          <label
            key={index}
            className={arClass(`flex items-center gap-2 px-3 py-2 rounded cursor-pointer text-sm border max-sm:text-xs ${
              darkMode
                ? "border-gray-700 bg-gray-800 text-white"
                : "border-gray-300 bg-white text-gray-800"
            } ${
              filters.minPrice === range.min && filters.maxPrice === range.max
                ? darkMode
                  ? "bg-blue-500 text-white"
                  : "bg-blue-100 text-gray-900 border-blue-500"
                : ""
            }`)}
          >
            <input
              type="radio"
              name="priceRange"
              checked={
                filters.minPrice === range.min && filters.maxPrice === range.max
              }
              onChange={() => handleSelect(range)}
              className={arClass("accent-blue-500 max-sm:text-xs")}
            />
            {range.label}
          </label>
        ))}
      </div>
    </div>
  );
};

const FilterSide = ({
  searchTerm,
  onSearchChange,
  filters = {},
  onFilterChange = () => {},
  sortOptions = { sorting: "" },
  onSortChange = () => {},
  onClearFilters = () => {},
  darkMode = false,
  searchPlaceholder = "Search sessions by IP, device, browser...",
  filterTitle = "Filter Sessions",
  className = "",
  // single combined config: sections inside filterOptions
  filterOptions = {}, // NEW: filterOptions.sections = [{ key, label, type, options }]
  // removed: sortOptionsList
  // Optional custom labels for filter sections (kept for backward compat)
  filterLabels = {},
  // type map: 'buttons'|'select'|'input' (kept for backward compat)
  filterTypes = {},
  // columns control from parent (e.g. visibleColumns from Medicaments)
  visibleColumns = null,
  onToggleColumn = () => {},
  viewMode = "table",
}) => {
  
  const [showFilters, setShowFilters] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const filterSideRef = useRef(null);
  const defaultLanguage = useSelector((state) => state.settings.defaultLanguage);
  useLockBodyScroll(showFilters);
  const isRTL = defaultLanguage === "ar";
  const slideDirection = isRTL ? "-100%" : "100%";
  const getSections = () => {
    // If caller provided explicit sections array inside filterOptions, use that (preferred)
    if (Array.isArray(filterOptions?.sections)) {
      return filterOptions.sections;
    }

    // Backward-compat: if filterOptions is an object of key->options (old shape),
    // convert to sections array
    const keys = Object.keys(filterOptions || {});
    return keys.map((key) => {
      const options = filterOptions[key];
      // allow filterTypes.filterOptions[key] to override
      const explicitType = (filterTypes?.filterOptions || {})[key];
      // if key === 'sortOptions' or key === 'sort' treat as sort section
      const typeGuess =
        explicitType ||
        (key === "sortOptions" || key === "sort"
          ? "sort"
          : Array.isArray(options)
          ? "buttons"
          : "input");
      return {
        key,
        label:
          filterLabels[key] ||
          (typeof key === "string"
            ? key.charAt(0).toUpperCase() + key.slice(1)
            : key),
        type: typeGuess,
        options: options,
      };
    });
  };
  // Build default filter values from filterOptions (supports new sections API and legacy shape)
  const defaultFilters = useMemo(() => {
    const defs = {};
    const sections = getSections();
    sections.forEach((s) => {
      const k = s.key;
      const t = s.type;
      const opts = s.options;

      if (t === "input") {
        defs[k] = "";
      } else if (t === "select" || t === "buttons" || t === "sort") {
        defs[k] =
          Array.isArray(opts) && opts.length > 0 ? opts[0].value ?? "" : "";
      } else if (t === "price" || k === "price" || k === "priceRange") {
        // price map to minPrice/maxPrice defaults
        const first = Array.isArray(opts) && opts.length > 0 ? opts[0] : null;
        defs.minPrice = first?.min ?? null;
        defs.maxPrice = first?.max ?? null;
      } else {
        // fallback: treat as buttons/select — default to first option if present, otherwise empty
        defs[k] =
          Array.isArray(opts) && opts.length > 0 ? opts[0].value ?? "" : "";
      }
    });
    return defs;
  }, [filterOptions, filterTypes, filterLabels]);

  // Count filters that differ from their default values (only those count as "active")
  const activeFiltersCount = useMemo(() => {
  let count = 0;

  Object.keys(defaultFilters).forEach((key) => {
    const def = defaultFilters[key];
    const val = filters?.[key];

    // Ignore empty string or null/undefined
    if (val === "" || val === null || val === undefined) return;

    if (def === null || def === undefined) {
      // default is "unset" -> count any non-empty value
      count++;
    } else if (typeof def === "number") {
      if (Number(val) !== Number(def)) count++;
    } else {
      if (val !== def) count++;
    }
  });

  return count;
}, [filters, defaultFilters]);




  const hasActiveFilters = activeFiltersCount > 0;

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Close filters when clicking outside (for mobile overlay)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMobile &&
        showFilters &&
        filterSideRef.current &&
        !filterSideRef.current.contains(event.target)
      ) {
        setShowFilters(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showFilters, isMobile]);

  const handleSearchChange = (e) => {
    onSearchChange(e.target.value);
  };

  const handleFilterToggle = () => {
    setShowFilters(!showFilters);
  };

  // Animation variants
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  const mobileSidebarVariants = {
    hidden: { x: slideDirection },
    visible: {
      x: 0,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 300,
      },
    },
    exit: {
      x: slideDirection,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 300,
      },
    },
  };

  const desktopSidebarVariants = {
    hidden: { x: slideDirection },
    visible: {
      x: 0,
      transition: {
        type: "spring",
        damping: 30,
        stiffness: 300,
      },
    },
    exit: {
      x: slideDirection,
      transition: {
        type: "spring",
        damping: 30,
        stiffness: 300,
      },
    },
  };

  // Build normalized sections array (new API) with fallback for old API shapes

  // render single generic section descriptor { key, label, type, options }
  const renderSection = (section ,i) => {
    const { key, label, type, options } = section;
    if (type === "price" || key === "price" || key === "priceRange") {
      return (
        <div className="mb-6" key={key + i}>
          <PriceRangeSelector
            filters={filters}
            darkMode={darkMode}
            priceRanges={options}
            onChange={(min, max) => {
              
              onFilterChange("minPrice", min);
              onFilterChange("maxPrice", max);
            }}
          />
        </div>
      );
    }
    if (type === "columns") {
      return <div key={key + i}>{renderColumnsSection()}</div>;
    }

    if (type === "input") {
      const currentValue = filters?.[key] ?? "";
      return (
        <div className={arClass("mb-6")} key={key + i}>
          <p className={arClass("uppercase text-xs font-semibold mb-2 opacity-70 max-sm:text-xs")}>
            {label}
          </p>
          <input
            type="text"
            placeholder={section.options?.placeholder || ""}
            value={currentValue}
            onChange={(e) => onFilterChange(key, e.target.value)}
            className={arClass(`w-full px-3 py-2 rounded-md text-sm max-sm:text-xs ${
              darkMode
                ? "!bg-gray-800 !text-gray-100 !placeholder-gray-400"
                : "!bg-white !text-gray-800"
            } border ${darkMode ? "!border-gray-700" : "!border-gray-200"}`)}
          />
        </div>
      );
    }

    if (type === "select") {
      const currentValue = filters?.[key] ?? options?.[0]?.value ?? "";
      return (
        <div className={arClass("mb-6")} key={key + i}>
          <p className={arClass("uppercase text-xs font-semibold mb-2 opacity-70 max-sm:text-xs")}>
            {label}
          </p>
          <select
            value={currentValue}
            onChange={(e) => onFilterChange(key, e.target.value)}
            className={arClass(`w-full px-3 py-2 rounded-md text-sm border  max-sm:text-xs ${
              darkMode
                ? "bg-gray-800 text-gray-100 border-gray-700"
                : "bg-white text-gray-800 border-gray-200"
            }`)}
          >
            {(options || []).map((o) => (
              <option className={arClass("px-0.5")} key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (type === "sort" || key === "sortOptions" || key === "sort") {
      // treat as sort buttons list (calls onSortChange)
      return (
        <div className={arClass("mb-6")} key={key + i}>
          <p className={arClass("uppercase text-xs font-semibold mb-2 opacity-70")}>
            {label}
          </p>
          <div className={arClass("flex flex-col gap-2")}>
            {(options || []).map((option, index) => (
              <motion.button
                key={option.value}
                onClick={() => onSortChange(option.value)}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 * index }}
                className={arClass(`flex items-center justify-between w-full px-3 py-3 text-left text-sm rounded-md border transition-all duration-300 max-sm:text-xs ${
                  sortOptions.sorting === option.value
                    ? darkMode
                      ? "!bg-slate-400 !border-gray-800 !text-gray-800"
                      : "!bg-slate-600 border-gray-300 !text-white"
                    : darkMode
                    ? "!bg-gray-800 border-gray-300 !text-white hover:!border-gray-400 hover:!bg-gray-400 hover:!text-black"
                    : "border-gray-300 !text-black hover:!border-gray-600 hover:!bg-gray-500 hover:!text-white"
                }`)}
              >
                {option.label}
                {sortOptions.sorting === option.value && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    className={arClass("px-0.5")}
                  >
                    <FaCheck />
                  </motion.div>
                )}
              </motion.button>
            ))}
          </div>
        </div>
      );
    }

    // default: buttons (same as previous renderFilterSection logic)
    const currentValue = filters?.[key] ?? "all";
    return (
      <div className={arClass("mb-6")} key={key + i}>
        <p className={arClass("uppercase text-xs font-semibold mb-2 opacity-70")}>
          {label}
        </p>
        <div className={arClass("flex flex-col gap-2")}>
          {(options || []).map((option) => (
            <motion.button
              key={option.value}
              onClick={() => onFilterChange(key, option.value)}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.98 }}
              className={arClass(`flex items-center justify-between w-full px-3 py-3 text-left text-sm rounded-md border transition-all duration-300 max-sm:p-2 max-sm:text-xs ${
                currentValue === option.value
                  ? darkMode
                    ? "!bg-slate-400 !border-gray-800 !text-gray-800"
                    : "!bg-slate-600 border-gray-300 !text-white"
                  : darkMode
                  ? "!bg-gray-800 border-gray-300 !text-white hover:!border-gray-400 hover:!bg-gray-400 hover:!text-black"
                  : "border-gray-300 !text-black hover:!border-gray-600 hover:!bg-gray-500 hover:!text-white"
              }`)}
            >
              {option.label}
              {currentValue === option.value && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  className={arClass("px-0.5")}
                >
                  <FaCheck />
                </motion.div>
              )}
            </motion.button>
          ))}
        </div>
      </div>
    );
  };

  const renderColumnsSection = () => {
  if (!visibleColumns) return null;

  return (
    <div className={arClass("mb-6")}>
      <p
        className={arClass(
          "uppercase text-xs font-semibold mb-2 opacity-70 max-sm:text-xs"
        )}
      >
        {filterLabels.columns || "Columns"}
      </p>

      <div className={arClass("grid grid-cols-2 sm:grid-cols-3 gap-3")}>
        {Object.entries(visibleColumns).map(([columnKey, columnValue], idx) => {
          // 🔹 normalize old & new versions
          const isObject = typeof columnValue === "object";

          const isVisible = isObject
            ? columnValue.visible
            : columnValue;

          const label = isObject
            ? columnValue.label
            : columnKey;

          return (
            <label
              key={`${columnKey}_${idx}`}
              className={`inline-flex items-center cursor-pointer col-span-1 max-sm:text-xs ${
                idx === 6 ? "col-span-2" : ""
              }`}
            >
              <input
                type="checkbox"
                className="sr-only peer"
                checked={!!isVisible}
                onChange={() => onToggleColumn(columnKey)}
                disabled={viewMode === "grid"}
              />

              <div
                className={`
                  relative w-11 h-6 max-sm:w-9 max-sm:h-4 rounded-full shadow-inner transition-colors
                  after:content-[''] after:absolute
                  after:top-0.5 max-sm:after:top-[1px]
                  after:left-[2px]
                  after:bg-white after:border-gray-300 after:border after:rounded-full
                  after:h-5 after:w-5 max-sm:after:h-3.5 max-sm:after:w-3.5
                  after:transition-transform
                  rtl:peer-checked:after:-translate-x-full
                  peer-checked:after:translate-x-full
                  max-sm:peer-checked:after:translate-x-[17px]
                  ${
                    viewMode === "grid"
                      ? "opacity-30 cursor-not-allowed bg-gray-300"
                      : "bg-gray-200 peer-checked:bg-green-600"
                  }
                `}
              />

              <span
                className={`ms-3 text-sm font-medium max-sm:text-xs capitalize ${
                  viewMode === "grid"
                    ? "opacity-30 cursor-not-allowed"
                    : ""
                } ${
                  isVisible
                    ? darkMode
                      ? "text-green-200"
                      : "text-green-600"
                    : darkMode
                    ? "text-gray-100"
                    : "text-gray-900"
                }`}
              >
                {label}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

  return (
    <div className={arClass(`flex flex-col gap-4 w-full ${className}`)}>
      {/* Search and Filter Button Bar */}
      <div className={arClass("flex items-center gap-4 max-sm:flex-col w-full")}>
        {/* Search Input */}
        <div className={arClass("flex-[3] flex items-center gap-2 max-2xl:w-full max-sm:flex-1")}>
          <motion.div
            whileHover={{ scale: 1.005 }}
            className={arClass(`flex gap-2 items-center p-3 rounded-lg w-8/12 max-2xl:w-10/12 max-xl:w-10/12 max-lg:w-full border transition-colors max-sm:py-2 max-sm:rounded-md ${
              darkMode
                ? "bg-gray-900 border-gray-300"
                : "!bg-gray-100 border-gray-300"
            }`)}
          >
            <FaSearch
              className={arClass(darkMode ? "text-gray-400" : "text-gray-500")}
            />
            <input
              className={arClass(`rounded-lg w-full bg-transparent border-none focus:outline-none focus:ring-0 !bg-t max-sm:text-xs ${
                darkMode
                  ? "text-gray-100 placeholder-gray-400"
                  : "text-gray-900 placeholder-gray-500"
              }`)}
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </motion.div>
        </div>

        {/* Filter Button */}
        <div className={arClass("flex-1 flex items-center gap-4 justify-end max-w-[400px] max-sm:!w-full max-sm:!max-w-full ")}>
          <div className={arClass("flex items-center gap-2 w-full ")}>
            <motion.button
              onClick={handleFilterToggle}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={arClass(`flex-[2] w-max flex items-center justify-between jus gap-2 px-4 py-3 rounded-lg border transition-all duration-300 max-sm:!text-xs max-sm:py-2 max-sm:rounded-md ${
                darkMode
                  ? "!bg-gray-900 !border-gray-300 hover:!bg-gray-600"
                  : "!bg-gray-100 !text-gray-600 hover:!bg-gray-200"
              } ${hasActiveFilters ? "!bg-blue-100 !border-blue-300 " : ""}`)}
            >
              <div className={arClass("flex items-center gap-2")}>
                <FaFilter
                  className={
                    arClass(hasActiveFilters ? "text-blue-600 dark:text-blue-400" : "")
                  }
                />
                {
                  defaultLanguage === "ar" ? "الفلاتر" : defaultLanguage === "fr" ? "Filtres" : "Filters"
                }
              </div>
              {hasActiveFilters && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  className={arClass("bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center")}
                >
                  {activeFiltersCount}
                </motion.span>
              )}
            </motion.button>

            <AnimatePresence>
              {hasActiveFilters && (
                <motion.button
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: "auto" }}
                  exit={{ opacity: 0, width: 0 }}
                  onClick={onClearFilters}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={arClass(`px-6 py-3 rounded-lg w-max font-semibold border transition-all duration-200 shadow-sm hover:shadow-sm max-sm:p-2 max-sm:text-xs max-sm:rounded-md min-w-max max-sm:min-w-maw ${
                    darkMode
                      ? "!bg-red-900/40 text-red-600 border-red-600 hover:bg-red-600"
                      : "!bg-red-300 text-red-700 border-red-700 hover:bg-red-50"
                  }`)}
                >
                  {
                    defaultLanguage === "ar" ? "مسح الفلاتر" : defaultLanguage === "fr" ? "Effacer les filtres" : "Clear Filters"
                  }
                </motion.button>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Filter Sidebar/Overlay */}
      <AnimatePresence>
        {showFilters && isMobile && (
          <motion.div
            className={arClass("fixed inset-0 z-[1100]")}
            initial="hidden"
            animate="visible"
            exit="hidden"
          >
            {/* Overlay */}
            <motion.div
              variants={overlayVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              transition={{ duration: 0.2 }}
              className={arClass("absolute inset-0 bg-black/40 backdrop-blur-sm")}
              onClick={() => setShowFilters(false)}
            />

            {/* Mobile Sidebar */}
            <motion.div
              ref={filterSideRef}
              variants={mobileSidebarVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className={arClass(`absolute ${isRTL ? 'left-0' : 'right-0'} top-0 h-full w-full max-w-md p-8  ${isRTL ? 'shadow-2xl border-r' : 'shadow-2xl border-l'} max-sm:p-4 ${
                darkMode
                  ? "bg-gray-900 border-gray-700 text-gray-100"
                  : "bg-white border-gray-200 text-gray-800"
              }`)}
            >
              {/* Mobile Header */}
              <motion.div
                className={arClass("flex justify-between items-center mb-6")}
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
              >
                <h2 className={arClass("text-2xl font-semibold text-blue-500 max-sm:text-lg")}>
                  {filterTitle}
                </h2>
                <motion.button
                  onClick={() => setShowFilters(false)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className={arClass(`p-2 rounded-lg font-semibold border transition-all duration-200 max-sm:text-xs ${
                    darkMode
                      ? "bg-red-700/40 text-red-400 border-red-600 hover:bg-red-600"
                      : "bg-red-200 text-red-600 border-red-600 hover:bg-red-50"
                  }`)}
                >
                  <FaTimes />
                </motion.button>
              </motion.div>

              <motion.div
                className={arClass("h-[calc(100vh-8rem)] overflow-y-auto")}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                {/* Filters */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className={arClass(`p-6 rounded-xl border mb-6 ${
                    darkMode
                      ? "border-gray-700 bg-gray-850"
                      : "border-gray-200 bg-gray-50"
                  }`)}
                >
                  <h3 className={arClass("text-lg font-semibold mb-4 text-blue-500 uppercase tracking-wide max-sm:text-sm")}>
                    {filterLabels.filterBy || (defaultLanguage === "ar" ? "تصفية حسب" : defaultLanguage === "fr" ? "Filtrer par" : "Filter By")}
                  </h3>
                  {getSections().map(renderSection)}
                </motion.div>

                {/* Price / Columns */}
                {/* If columns provided as a section, render it via getSections(); otherwise fallback to explicit columns rendering */}
                {(!filterOptions?.sections ||
                  !filterOptions.sections.some((s) => s.type === "columns")) &&
                  renderColumnsSection()}
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop Sidebar */}
      <AnimatePresence>
        {showFilters && !isMobile && (
          <motion.div
            initial="hidden"
            animate="visible"
            exit="exit"
              className={arClass(`fixed ${isRTL ? 'left-0' : 'right-0'} top-0 h-full z-[1100]`)}
          >
            {/* Overlay */}
            <motion.div
              variants={overlayVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              transition={{ duration: 0.2 }}
              className={arClass("fixed inset-0 bg-black/20 ")}
              onClick={() => setShowFilters(false)}
            />

            {/* Desktop Sidebar */}
            <div className={arClass(`w-full h-full flex ${isRTL ? 'justify-start' : 'justify-end'}`)}>
              <motion.div
                ref={filterSideRef}
                variants={desktopSidebarVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className={arClass(`relative w-[600px] h-full p-8 ${isRTL ? 'rounded-r-2xl border-r' : 'rounded-l-2xl border-l'} shadow-2xl ${
                  darkMode
                    ? "bg-gray-900 border-gray-700 text-gray-100"
                    : "bg-white border-gray-200 text-gray-800"
                }`)}
              >
                {/* Desktop Header */}
                <motion.div
                  className={arClass("flex justify-between items-center mb-6")}
                  initial={{ y: -20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.1 }}
                >
                  <h2 className={arClass("text-2xl font-semibold text-blue-500")}>
                    {filterTitle}
                  </h2>
                  <motion.button
                    onClick={() => setShowFilters(false)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className={arClass(`p-3 rounded-lg font-semibold border transition-all duration-200 ${
                      darkMode
                        ? "bg-red-700/40 text-red-400 border-red-600 hover:bg-red-600"
                        : "bg-red-200 text-red-600 border-red-600 hover:bg-red-50"
                    }`)}
                  >
                    <FaTimes />
                  </motion.button>
                </motion.div>

                <motion.div
                  className={arClass("h-[calc(100vh-8rem)] overflow-y-auto")}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  {/* Filters */}
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className={arClass(`p-6 rounded-xl border mb-6 ${
                      darkMode
                        ? "border-gray-700 bg-gray-850"
                        : "border-gray-200 bg-gray-50"
                    }`)}
                  >
                    <h3 className={arClass("text-lg font-semibold mb-4 text-blue-500 uppercase tracking-wide max-sm:text-sm")}>
                      {filterLabels.filterBy || (defaultLanguage === "ar" ? "تصفية حسب" : defaultLanguage === "fr" ? "Filtrer par" : "Filter By")}
                    </h3>
                    {getSections().map(renderSection)}
                  </motion.div>

                  {/* Price / Columns */}
                  {/* If columns provided as a section, render it via getSections(); otherwise fallback to explicit columns rendering */}
                  {(!filterOptions?.sections ||
                    !filterOptions.sections.some(
                      (s) => s.type === "columns"
                    )) &&
                    renderColumnsSection()}
                </motion.div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FilterSide;
